import defaultImg from '../../assets/user_default.svg';
import CancelTicket from '../../components/CancelTicket/CancelTicket';
import PageLoader from '../../components/PageLoader/PageLoader';
import DEFAULT_EVENT_IMAGE_URL from '../../constants/imageConstants';
import { TableContext } from '../../contexts/tableContext';
import envValues from '../../enviornment';
import { getCookies } from '../../hooks/useCookies';
import { SERVER_BASE_URL } from '../../redux/apiConstants';
import {
  formatEventDateOrdinalTz,
  formatEventTime,
  formatTime,
} from '../../utilityFunctions/utilsFunctions';
import styles from './viewEvent.module.scss';
import axios from 'axios';
import { useEffect, useState, useContext } from 'react';
import { Image } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

/** Labels aligned with shows listing filters (ShowCard EVENT_TYPE_FILTERS). */
const EVENT_TYPE_LABELS = {
  booking: 'Seat Booking',
  'non-seat-booking': 'Non-Seat Booking',
  rsvp: 'RSVP',
  form: 'Registration',
  token: 'Token',
};

/** Same location rules as `ShowCard` listing cards. */
const getLocationDisplay = (item) => {
  if (!item || typeof item !== 'object') {
    return { online: false, text: '\u2014' };
  }
  const { onlineDetails } = item;
  const isOnline = onlineDetails && (onlineDetails.platform || onlineDetails.streamingUrl);
  if (isOnline) {
    const { platform } = onlineDetails;
    const platformLabel = platform ? `${platform.charAt(0).toUpperCase()}${platform.slice(1)}` : '';
    return {
      online: true,
      text: platform ? `Online \u2022 ${platformLabel}` : 'Online \u2022 Streaming',
    };
  }
  if (item.location) {
    const parts = [item.location.venue, item.location.city, item.location.country].filter(Boolean);
    return { online: false, text: parts.length > 0 ? parts.join(', ') : '\u2014' };
  }
  return { online: false, text: '\u2014' };
};

const getListingStatusLabel = (item) => {
  if (!item || typeof item !== 'object') return '\u2014';
  if (item.bookingPaused) return 'Paused';
  if (item.bookingStatus === 'sold-out') return 'Sold-out';
  if (item.status === 'created') return 'Upcoming';
  if (item.status === 'in progress') return 'Ongoing';
  if (item.status === 'cancelled') return 'Cancelled';
  if (item.status === 'ended') return 'Ended';
  return '\u2014';
};

const getEventCategoryLabel = (category) =>
  (category && EVENT_TYPE_LABELS[category]) || category || '\u2014';

const getListingStatusBadgeBackground = (item) => {
  if (!item || typeof item !== 'object') return '#e7f3ff';
  if (item.bookingPaused) return '#fff3cd';
  if (item.bookingStatus === 'sold-out' || item.status === 'ended' || item.status === 'cancelled') {
    return '#e8eaed';
  }
  return '#e7f3ff';
};

const BOOKING_PAUSE_EVENT_TYPES = ['form', 'booking', 'non-seat-booking', 'rsvp'];

const supportsBookingPause = (item) =>
  BOOKING_PAUSE_EVENT_TYPES.includes((item?.eventType || '').toLowerCase());

const canPauseBookingForEvent = (item) =>
  supportsBookingPause(item) &&
  ['created', 'in progress'].includes(item?.status) &&
  !item?.bookingPaused &&
  item?.bookingStatus !== 'ended';

const canResumeBookingForEvent = (item) => item?.bookingPaused === true;

function ViewEvent() {
  const { dashboardStyle } = useContext(TableContext);
  const { setDashboardHeader } = useContext(TableContext);
  const { bodyStyle } = useContext(TableContext);
  const { formthemeStyle } = useContext(TableContext);
  const { viewformStyle } = useContext(TableContext);
  const params = useParams();
  const navigate = useNavigate();
  const token = `Bearer ${getCookies('Token')}`;
  const [loading, setLoading] = useState(false);
  const [cancelEventLoading, setCancelEventLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const [eventid, seteventId] = useState(null);
  const [tokens, setTokens] = useState(0);
  const [cancelEventModal, setCancelEventModal] = useState(false);
  const [bookingToggleLoading, setBookingToggleLoading] = useState(false);

  const getUserDetails = async (showId) => {
    if (!showId) return;
    setLoading(true);
    setDetails(null);
    const headers = { Authorization: token };
    try {
      const showRes = await axios.get(`${envValues.REACT_APP_API_ENDPOINT}/shows/admin/${showId}`, {
        headers,
      });
      if (showRes.status === 200 && showRes.data?.data?.event) {
        const ev = showRes.data.data.event;
        setDetails(ev);
        seteventId(ev._id || ev.id);
        const totalTokens = showRes.data.data.tokens;
        setTokens(typeof totalTokens === 'number' ? totalTokens : 0);
      } else {
        toast.error('Show not found.');
        setDetails(null);
        seteventId(null);
        setTokens(0);
      }
    } catch {
      toast.error('Failed to load show.');
      setDetails(null);
      seteventId(null);
      setTokens(0);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEvent = async () => {
    const showId = eventid || details?.id || details?._id;
    if (!showId) return;
    setCancelEventLoading(true);
    try {
      const response = await axios.post(
        `${SERVER_BASE_URL}/admin/events/${showId}/cancel`,
        {},
        { headers: { Authorization: token } }
      );
      if (response.data?.success) {
        toast.success(
          details?.eventType === 'token'
            ? 'Event has been cancelled successfully.'
            : 'Event has been cancelled and refunds have been initiated.'
        );
        setCancelEventModal(false);
        navigate(-1);
      } else {
        toast.error(response.data?.message || 'Failed to cancel the event.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'An error occurred while cancelling the event.');
    } finally {
      setCancelEventLoading(false);
    }
  };

  function copyToClipboard(text) {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success('Event code is copied to clipboard.');
      })
      .catch(() => {
        toast.error('Could not copy to clipboard.');
      });
  }

  const handleOpenCancelEventModal = () => setCancelEventModal(true);

  const handleCloseCancelEventModal = () => setCancelEventModal(false);

  const handleToggleBookingPause = async (action) => {
    const showId = eventid || details?.id || details?._id;
    if (!showId) return;
    setBookingToggleLoading(true);
    try {
      const endpoint =
        action === 'pause'
          ? `${SERVER_BASE_URL}/shows/pause-booking/${showId}`
          : `${SERVER_BASE_URL}/shows/resume-booking/${showId}`;
      const { data } = await axios.post(endpoint, {}, { headers: { Authorization: token } });
      const updatedShow = data?.data?.show;
      if (updatedShow) {
        setDetails((prev) => ({ ...prev, ...updatedShow }));
      } else {
        await getUserDetails(showId);
      }
      toast.success(action === 'pause' ? 'Booking paused.' : 'Booking resumed.');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update booking status.');
    } finally {
      setBookingToggleLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    setDashboardHeader('Event Details');
  }, []);

  useEffect(() => {
    if (!params.eventId) return;
    getUserDetails(params.eventId);
  }, [params.eventId]);

  const isTokenEvent = details?.eventType === 'token';
  const cancelEventConfirmMessage = (() => {
    const eventName =
      details?.eventName != null && String(details.eventName).trim() !== ''
        ? `the event "${details.eventName}"`
        : 'this event';
    if (isTokenEvent) {
      return `Are you sure you want to cancel ${eventName}? This action cannot be undone.`;
    }
    return `Are you sure you want to cancel ${eventName}? This will refund all booked tickets and cannot be undone.`;
  })();

  return (
    <div className={styles[bodyStyle]}>
      <CancelTicket
        show={cancelEventModal}
        handleClose={handleCloseCancelEventModal}
        onConfirm={handleCancelEvent}
        onCancel={handleCloseCancelEventModal}
        message={cancelEventConfirmMessage}
        loading={cancelEventLoading}
      />
      <div className={styles[dashboardStyle]} id={styles.dashboardcont2}>
        <div className="form_head">
          <div
            value="Back"
            onClick={() => {
              navigate(-1);
            }}
            className={`${styles.arrowback} backBtn`}
          >
            <svg
              className="mr-1"
              width="17"
              height="14"
              viewBox="0 0 17 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.5 6.00003H3.64L7.27 1.64003C7.43974 1.43581 7.5214 1.17253 7.49702 0.908108C7.47264 0.643682 7.34422 0.39977 7.14 0.230032C6.93578 0.060293 6.6725 -0.0213689 6.40808 0.0030108C6.14365 0.0273905 5.89974 0.155815 5.73 0.360032L0.73 6.36003C0.696361 6.40776 0.666279 6.45789 0.64 6.51003C0.64 6.56003 0.64 6.59003 0.57 6.64003C0.524674 6.75469 0.500941 6.87674 0.5 7.00003C0.500941 7.12332 0.524674 7.24537 0.57 7.36003C0.57 7.41003 0.57 7.44003 0.64 7.49003C0.666279 7.54217 0.696361 7.59231 0.73 7.64003L5.73 13.64C5.82402 13.7529 5.94176 13.8437 6.07485 13.9059C6.20793 13.9681 6.35309 14.0003 6.5 14C6.73365 14.0005 6.96009 13.9191 7.14 13.77C7.24126 13.6861 7.32496 13.583 7.38631 13.4666C7.44766 13.3503 7.48546 13.223 7.49754 13.092C7.50961 12.961 7.49573 12.829 7.45669 12.7034C7.41764 12.5777 7.3542 12.4611 7.27 12.36L3.64 8.00003H15.5C15.7652 8.00003 16.0196 7.89467 16.2071 7.70714C16.3946 7.5196 16.5 7.26525 16.5 7.00003C16.5 6.73482 16.3946 6.48046 16.2071 6.29292C16.0196 6.10539 15.7652 6.00003 15.5 6.00003Z"
                fill=""
              />
            </svg>
            Back
          </div>
          {details?.active === true &&
            (details?.status === 'created' || details?.status === 'in progress') && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '12px',
                  flexWrap: 'wrap',
                }}
              >
                {canPauseBookingForEvent(details) && (
                  <span className="sub_btn2">
                    <input
                      className={styles.formbtncalloff}
                      type="button"
                      disabled={bookingToggleLoading}
                      onClick={() => handleToggleBookingPause('pause')}
                      value={bookingToggleLoading ? 'Please wait...' : 'Pause Booking'}
                    />
                  </span>
                )}
                {canResumeBookingForEvent(details) && (
                  <span className="sub_btn2">
                    <input
                      className={styles.formbtncalloff}
                      type="button"
                      disabled={bookingToggleLoading}
                      onClick={() => handleToggleBookingPause('resume')}
                      value={bookingToggleLoading ? 'Please wait...' : 'Resume Booking'}
                    />
                  </span>
                )}
                <div>
                  <span className="sub_btn2 deletebtn">
                    <input
                      className={styles.formbtncalloff}
                      type="button"
                      onClick={handleOpenCancelEventModal}
                      value="Cancel"
                    />
                  </span>
                </div>
              </div>
            )}
        </div>
        <div
          className={` ${styles.profile_view_container} profile_view_container`}
          id={styles.addform}
        >
          {loading && <PageLoader fullPage />}
          {!loading && !details && <p className="mt-4 text-center">No show data to display.</p>}
          {!loading && details && (
            <section>
              <div className="">
                <div className="row">
                  <div className="col-lg-4 col-xs-6 mb-5">
                    <div className={styles.eventcard_sec}>
                      <div className={`${styles.event_card_div} thum text-center event_card_div`}>
                        <Image src={details.image || DEFAULT_EVENT_IMAGE_URL} alt="" />
                      </div>
                      <div style={{ marginTop: '10px' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: 600,
                            background: getListingStatusBadgeBackground(details),
                            color: '#132941',
                          }}
                        >
                          {getListingStatusLabel(details)}
                        </span>
                      </div>
                      <p className={styles.eventnamep}>Event Name</p>
                      <h5 className={styles.electroh5}>{details.eventName || '\u2014'}</h5>
                      <p className={styles.location}>Location</p>
                      <div
                        className={styles.gala}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          flexWrap: 'wrap',
                        }}
                      >
                        {(() => {
                          const loc = getLocationDisplay(details);
                          let locationIcon;
                          if (loc.online) {
                            locationIcon = (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#132941"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden
                              >
                                <circle cx="12" cy="12" r="10" />
                                <line x1="2" y1="12" x2="22" y2="12" />
                                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                              </svg>
                            );
                          } else {
                            locationIcon = (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="19"
                                viewBox="0 0 18 19"
                                fill="none"
                                aria-hidden
                              >
                                <path
                                  d="M9 16.9762C13.1421 16.9762 16.5 15.4652 16.5 13.6012C16.5 12.6513 15.6278 11.7929 14.2241 11.1796C13.3675 12.7515 12.0613 14.1064 10.4021 14.8154C9.51032 15.1965 8.48968 15.1965 7.59786 14.8154C5.93871 14.1064 4.6325 12.7515 3.77594 11.1796C2.37221 11.7929 1.5 12.6513 1.5 13.6012C1.5 15.4652 4.85786 16.9762 9 16.9762Z"
                                  fill="#132941"
                                />
                                <path
                                  fillRule="evenodd"
                                  clipRule="evenodd"
                                  d="M3.75 6.86199C3.75 4.16354 6.10051 1.97601 9 1.97601C11.8995 1.97601 14.25 4.16354 14.25 6.86199C14.25 9.53929 12.5744 12.6635 9.96005 13.7807C9.35061 14.0411 8.64939 14.0411 8.03995 13.7807C5.42562 12.6635 3.75 9.53929 3.75 6.86199ZM9 8.72601C9.82843 8.72601 10.5 8.05444 10.5 7.22601C10.5 6.39759 9.82843 5.72601 9 5.72601C8.17157 5.72601 7.5 6.39759 7.5 7.22601C7.5 8.05444 8.17157 8.72601 9 8.72601Z"
                                  fill="#132941"
                                />
                              </svg>
                            );
                          }
                          return (
                            <>
                              {locationIcon}
                              <span>{loc.text}</span>
                            </>
                          );
                        })()}
                      </div>
                      <p className={styles.eventnamep} style={{ marginTop: '12px' }}>
                        Schedule
                      </p>
                      <p className={styles.guild} style={{ marginBottom: 0 }}>
                        {details?.startDate && !Number.isNaN(new Date(details.startDate).getTime())
                          ? formatEventDateOrdinalTz(
                              details.startDate,
                              details?.timezone || details?.timezoneCode
                            )
                          : '\u2014'}{' '}
                        | {details?.startTime != null ? formatTime(details.startTime) : '\u2014'}{' '}
                        {details?.timezoneCode ?? ''}
                      </p>
                      <div className={styles.copyclip}>
                        <input type="text" value={details.eventCode || ''} id="myInput" readOnly />
                        <div
                          style={{ cursor: 'pointer' }}
                          onClick={() => copyToClipboard(details.eventCode)}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="17"
                            viewBox="0 0 16 17"
                            fill="none"
                          >
                            <g clipPath="url(#clip0_2623_21182)">
                              <path
                                d="M6.48962 13.5C4.92761 13.5 3.65625 12.2286 3.65625 10.6666V3.83337H2.48962C1.47827 3.83337 0.65625 4.65527 0.65625 5.66663V14.6666C0.65625 15.678 1.47827 16.5 2.48962 16.5H10.8229C11.8342 16.5 12.6562 15.678 12.6562 14.6666V13.5H6.48962Z"
                                fill="black"
                              />
                              <path
                                d="M15.3229 2.33337C15.3229 1.32068 14.5022 0.5 13.4896 0.5H6.48962C5.47693 0.5 4.65625 1.32068 4.65625 2.33337V10.6666C4.65625 11.6793 5.47693 12.5 6.48962 12.5H13.4896C14.5022 12.5 15.3229 11.6793 15.3229 10.6666V2.33337Z"
                                fill="black"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_2623_21182">
                                <rect
                                  width="16"
                                  height="16"
                                  fill="white"
                                  transform="translate(0 0.5)"
                                />
                              </clipPath>
                            </defs>
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-8 col-xs-8 mb-5">
                    <div className="row">
                      <div className="col-lg-6">
                        <div className={styles.eventcategorybox}>
                          <div className={styles.txt}>
                            <p>Event Category</p>
                            <h5>{getEventCategoryLabel(details.eventCategory)}</h5>
                          </div>

                          <svg
                            className={styles.sm_icon}
                            xmlns="http://www.w3.org/2000/svg"
                            width="42"
                            height="42"
                            viewBox="0 0 42 42"
                            fill="none"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M6.5625 1.3125C3.663 1.3125 1.3125 3.663 1.3125 6.5625V14.4375C1.3125 17.337 3.663 19.6875 6.5625 19.6875H14.4375C17.337 19.6875 19.6875 17.337 19.6875 14.4375V6.5625C19.6875 3.663 17.337 1.3125 14.4375 1.3125H6.5625Z"
                              fill="#40189D"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M32.7798 2.33398C32.644 1.73648 32.1127 1.3125 31.5 1.3125C30.8873 1.3125 30.356 1.73648 30.2202 2.33398L29.494 5.52755C29.1581 7.00468 28.0047 8.15811 26.5275 8.49398L23.334 9.22018C22.7365 9.35602 22.3125 9.88726 22.3125 10.5C22.3125 11.1127 22.7365 11.644 23.334 11.7798L26.5275 12.506C28.0047 12.8419 29.1581 13.9953 29.494 15.4725L30.2202 18.666C30.356 19.2635 30.8873 19.6875 31.5 19.6875C32.1127 19.6875 32.644 19.2635 32.7798 18.666L33.506 15.4725C33.8419 13.9953 34.9953 12.8419 36.4725 12.506L39.666 11.7798C40.2635 11.644 40.6875 11.1127 40.6875 10.5C40.6875 9.88726 40.2635 9.35602 39.666 9.22018L36.4725 8.49398C34.9953 8.15811 33.8419 7.00468 33.506 5.52755L32.7798 2.33398Z"
                              fill="#40189D"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M1.3125 27.5625C1.3125 24.663 3.663 22.3125 6.5625 22.3125H14.4375C17.337 22.3125 19.6875 24.663 19.6875 27.5625V35.4375C19.6875 38.337 17.337 40.6875 14.4375 40.6875H6.5625C3.663 40.6875 1.3125 38.337 1.3125 35.4375V27.5625Z"
                              fill="#40189D"
                            />
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M27.5625 22.3125C24.663 22.3125 22.3125 24.663 22.3125 27.5625V35.4375C22.3125 38.337 24.663 40.6875 27.5625 40.6875H35.4375C38.337 40.6875 40.6875 38.337 40.6875 35.4375V27.5625C40.6875 24.663 38.337 22.3125 35.4375 22.3125H27.5625Z"
                              fill="#40189D"
                            />
                          </svg>

                          <div className={styles.bg_icons_evnt}>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="42"
                              height="42"
                              viewBox="0 0 42 42"
                              fill="#FBFBFB"
                            >
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M6.5625 1.3125C3.663 1.3125 1.3125 3.663 1.3125 6.5625V14.4375C1.3125 17.337 3.663 19.6875 6.5625 19.6875H14.4375C17.337 19.6875 19.6875 17.337 19.6875 14.4375V6.5625C19.6875 3.663 17.337 1.3125 14.4375 1.3125H6.5625Z"
                              />
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M32.7798 2.33398C32.644 1.73648 32.1127 1.3125 31.5 1.3125C30.8873 1.3125 30.356 1.73648 30.2202 2.33398L29.494 5.52755C29.1581 7.00468 28.0047 8.15811 26.5275 8.49398L23.334 9.22018C22.7365 9.35602 22.3125 9.88726 22.3125 10.5C22.3125 11.1127 22.7365 11.644 23.334 11.7798L26.5275 12.506C28.0047 12.8419 29.1581 13.9953 29.494 15.4725L30.2202 18.666C30.356 19.2635 30.8873 19.6875 31.5 19.6875C32.1127 19.6875 32.644 19.2635 32.7798 18.666L33.506 15.4725C33.8419 13.9953 34.9953 12.8419 36.4725 12.506L39.666 11.7798C40.2635 11.644 40.6875 11.1127 40.6875 10.5C40.6875 9.88726 40.2635 9.35602 39.666 9.22018L36.4725 8.49398C34.9953 8.15811 33.8419 7.00468 33.506 5.52755L32.7798 2.33398Z"
                              />
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M1.3125 27.5625C1.3125 24.663 3.663 22.3125 6.5625 22.3125H14.4375C17.337 22.3125 19.6875 24.663 19.6875 27.5625V35.4375C19.6875 38.337 17.337 40.6875 14.4375 40.6875H6.5625C3.663 40.6875 1.3125 38.337 1.3125 35.4375V27.5625Z"
                              />
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M27.5625 22.3125C24.663 22.3125 22.3125 24.663 22.3125 27.5625V35.4375C22.3125 38.337 24.663 40.6875 27.5625 40.6875H35.4375C38.337 40.6875 40.6875 38.337 40.6875 35.4375V27.5625C40.6875 24.663 38.337 22.3125 35.4375 22.3125H27.5625Z"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-6 mt-4 mt-lg-0">
                        <div className={styles.eventcategorybox}>
                          <div className={styles.txt}>
                            <p>Event Date</p>
                            <h5>
                              {details.startDate && details.endDate
                                ? `${formatEventDateOrdinalTz(
                                    details?.startDate,
                                    details?.timezone || details?.timezoneCode
                                  )} - ${formatEventDateOrdinalTz(
                                    details?.endDate,
                                    details?.timezone || details?.timezoneCode
                                  )}`
                                : 'N/A'}
                            </h5>
                          </div>

                          <svg
                            className={styles.sm_icon}
                            xmlns="http://www.w3.org/2000/svg"
                            width="45"
                            height="45"
                            viewBox="0 0 42 42"
                            fill="none"
                          >
                            <path
                              d="M33.251 8.75H31.501V5.25C31.501 4.78587 31.3166 4.34075 30.9884 4.01256C30.6602 3.68437 30.2151 3.5 29.751 3.5C29.2868 3.5 28.8417 3.68437 28.5135 4.01256C28.1854 4.34075 28.001 4.78587 28.001 5.25V8.75H14.001V5.25C14.001 4.78587 13.8166 4.34075 13.4884 4.01256C13.1602 3.68437 12.7151 3.5 12.251 3.5C11.7868 3.5 11.3417 3.68437 11.0135 4.01256C10.6854 4.34075 10.501 4.78587 10.501 5.25V8.75H8.75098C7.35859 8.75 6.02323 9.30312 5.03867 10.2877C4.0541 11.2723 3.50098 12.6076 3.50098 14V15.75H38.501V14C38.501 12.6076 37.9479 11.2723 36.9633 10.2877C35.9787 9.30312 34.6434 8.75 33.251 8.75Z"
                              fill="#3F9AE0"
                            />
                            <path
                              d="M3.50098 33.25C3.50098 34.6424 4.0541 35.9777 5.03867 36.9623C6.02323 37.9469 7.35859 38.5 8.75098 38.5H33.251C34.6434 38.5 35.9787 37.9469 36.9633 36.9623C37.9479 35.9777 38.501 34.6424 38.501 33.25V19.25H3.50098V33.25Z"
                              fill="#3F9AE0"
                            />
                          </svg>

                          <div className={styles.bg_icons_evnt}>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="42"
                              height="42"
                              viewBox="0 0 42 42"
                              fill="#FBFBFB"
                            >
                              <path
                                d="M33.251 8.75H31.501V5.25C31.501 4.78587 31.3166 4.34075 30.9884 4.01256C30.6602 3.68437 30.2151 3.5 29.751 3.5C29.2868 3.5 28.8417 3.68437 28.5135 4.01256C28.1854 4.34075 28.001 4.78587 28.001 5.25V8.75H14.001V5.25C14.001 4.78587 13.8166 4.34075 13.4884 4.01256C13.1602 3.68437 12.7151 3.5 12.251 3.5C11.7868 3.5 11.3417 3.68437 11.0135 4.01256C10.6854 4.34075 10.501 4.78587 10.501 5.25V8.75H8.75098C7.35859 8.75 6.02323 9.30312 5.03867 10.2877C4.0541 11.2723 3.50098 12.6076 3.50098 14V15.75H38.501V14C38.501 12.6076 37.9479 11.2723 36.9633 10.2877C35.9787 9.30312 34.6434 8.75 33.251 8.75Z"
                                fill=""
                              />
                              <path
                                d="M3.50098 33.25C3.50098 34.6424 4.0541 35.9777 5.03867 36.9623C6.02323 37.9469 7.35859 38.5 8.75098 38.5H33.251C34.6434 38.5 35.9787 37.9469 36.9633 36.9623C37.9479 35.9777 38.501 34.6424 38.501 33.25V19.25H3.50098V33.25Z"
                                fill=""
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="row mt-4">
                      <div className="col-lg-6">
                        <div className={styles.eventcategorybox}>
                          <div className={styles.txt}>
                            <p>Event Time</p>
                            <h5>
                              {`${formatEventTime(
                                details.startTime,
                                details.timezone || details.timezoneCode
                              )} - ${formatEventTime(
                                details.endTime,
                                details.timezone || details.timezoneCode
                              )}`}
                            </h5>
                          </div>

                          <svg
                            className={styles.sm_icon}
                            xmlns="http://www.w3.org/2000/svg"
                            width="42"
                            height="42"
                            viewBox="0 0 42 42"
                            fill="none"
                          >
                            <g clipPath="url(#clip0_2623_21253)">
                              <path
                                d="M21 42C32.595 42 42 32.595 42 21C42 9.40498 32.595 0 21 0C9.40498 0 0 9.40498 0 21C0 32.595 9.40498 42 21 42ZM19.5 9.00002C19.5 8.17499 20.175 7.49997 21 7.49997C21.825 7.49997 22.5 8.17499 22.5 9.00002V20.28L29.4375 25.83C30.0826 26.3475 30.1875 27.2925 29.67 27.9375C29.3775 28.305 28.9425 28.5 28.5 28.5C28.17 28.5 27.8399 28.3949 27.5625 28.17L20.0625 22.17C19.7101 21.885 19.5001 21.4575 19.5001 21V9.00002H19.5Z"
                                fill="#2BC155"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_2623_21253">
                                <rect width="42" height="42" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>

                          <div className={styles.bg_icons_evnt}>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="42"
                              height="42"
                              viewBox="0 0 42 42"
                              fill="#FBFBFB"
                            >
                              <g clipPath="url(#clip0_2623_21253)">
                                <path
                                  d="M21 42C32.595 42 42 32.595 42 21C42 9.40498 32.595 0 21 0C9.40498 0 0 9.40498 0 21C0 32.595 9.40498 42 21 42ZM19.5 9.00002C19.5 8.17499 20.175 7.49997 21 7.49997C21.825 7.49997 22.5 8.17499 22.5 9.00002V20.28L29.4375 25.83C30.0826 26.3475 30.1875 27.2925 29.67 27.9375C29.3775 28.305 28.9425 28.5 28.5 28.5C28.17 28.5 27.8399 28.3949 27.5625 28.17L20.0625 22.17C19.7101 21.885 19.5001 21.4575 19.5001 21V9.00002H19.5Z"
                                  fill=""
                                />
                              </g>
                              <defs>
                                <clipPath id="clip0_2623_21253">
                                  <rect width="42" height="42" fill="white" />
                                </clipPath>
                              </defs>
                            </svg>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-6 mt-4 mt-lg-0">
                        <div className={styles.eventcategorybox}>
                          <div className={styles.txt}>
                            <p>Total No. Of Attendees </p>
                            <h5>{details.attendeeCount}</h5>
                          </div>

                          <svg
                            className={styles.sm_icon}
                            xmlns="http://www.w3.org/2000/svg"
                            width="42"
                            height="42"
                            viewBox="0 0 42 42"
                            fill="none"
                          >
                            <g clipPath="url(#clip0_2623_21340)">
                              <path
                                d="M21 16.9502C24.9415 16.9502 28.1367 13.755 28.1367 9.81348C28.1367 5.87198 24.9415 2.67676 21 2.67676C17.0585 2.67676 13.8633 5.87198 13.8633 9.81348C13.8633 13.755 17.0585 16.9502 21 16.9502Z"
                                fill="#FF9330"
                              />
                              <path
                                d="M35.4375 16.9501C37.9293 16.9501 39.9492 14.9301 39.9492 12.4384C39.9492 9.94663 37.9293 7.92667 35.4375 7.92667C32.9457 7.92667 30.9258 9.94663 30.9258 12.4384C30.9258 14.9301 32.9457 16.9501 35.4375 16.9501Z"
                                fill="#FF9330"
                              />
                              <path
                                d="M6.5625 16.9501C9.05425 16.9501 11.0742 14.9301 11.0742 12.4384C11.0742 9.94663 9.05425 7.92667 6.5625 7.92667C4.07075 7.92667 2.05078 9.94663 2.05078 12.4384C2.05078 14.9301 4.07075 16.9501 6.5625 16.9501Z"
                                fill="#FF9330"
                              />
                              <path
                                d="M11.0078 21.0017C9.2318 19.5466 7.62341 19.7392 5.56992 19.7392C2.49867 19.7392 0 22.2232 0 25.2755V34.2342C0 35.5598 1.08199 36.6377 2.41254 36.6377C8.15686 36.6377 7.46484 36.7416 7.46484 36.3899C7.46484 30.0419 6.71295 25.3865 11.0078 21.0017Z"
                                fill="#FF9330"
                              />
                              <path
                                d="M22.9536 19.7721C19.3669 19.4729 16.2493 19.7755 13.5603 21.9951C9.06026 25.5996 9.92627 30.4528 9.92627 36.39C9.92627 37.9608 11.2043 39.2627 12.799 39.2627C30.1143 39.2627 30.8035 39.8213 31.8302 37.5474C32.167 36.7785 32.0747 37.0229 32.0747 29.6667C32.0747 23.8239 27.0156 19.7721 22.9536 19.7721Z"
                                fill="#FF9330"
                              />
                              <path
                                d="M36.43 19.7392C34.3653 19.7392 32.7658 19.5485 30.9922 21.0016C35.2549 25.3539 34.5351 29.6916 34.5351 36.3899C34.5351 36.7437 33.9607 36.6376 39.5013 36.6376C40.8794 36.6376 42 35.5212 42 34.1488V25.2755C42 22.2231 39.5013 19.7392 36.43 19.7392Z"
                                fill="#FF9330"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_2623_21340">
                                <rect width="42" height="42" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>

                          <div className={styles.bg_icons_evnt}>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="42"
                              height="42"
                              viewBox="0 0 42 42"
                              fill="#FBFBFB"
                            >
                              <g clipPath="url(#clip0_2623_21340)">
                                <path
                                  d="M21 16.9502C24.9415 16.9502 28.1367 13.755 28.1367 9.81348C28.1367 5.87198 24.9415 2.67676 21 2.67676C17.0585 2.67676 13.8633 5.87198 13.8633 9.81348C13.8633 13.755 17.0585 16.9502 21 16.9502Z"
                                  fill=""
                                />
                                <path
                                  d="M35.4375 16.9501C37.9293 16.9501 39.9492 14.9301 39.9492 12.4384C39.9492 9.94663 37.9293 7.92667 35.4375 7.92667C32.9457 7.92667 30.9258 9.94663 30.9258 12.4384C30.9258 14.9301 32.9457 16.9501 35.4375 16.9501Z"
                                  fill=""
                                />
                                <path
                                  d="M6.5625 16.9501C9.05425 16.9501 11.0742 14.9301 11.0742 12.4384C11.0742 9.94663 9.05425 7.92667 6.5625 7.92667C4.07075 7.92667 2.05078 9.94663 2.05078 12.4384C2.05078 14.9301 4.07075 16.9501 6.5625 16.9501Z"
                                  fill=""
                                />
                                <path
                                  d="M11.0078 21.0017C9.2318 19.5466 7.62341 19.7392 5.56992 19.7392C2.49867 19.7392 0 22.2232 0 25.2755V34.2342C0 35.5598 1.08199 36.6377 2.41254 36.6377C8.15686 36.6377 7.46484 36.7416 7.46484 36.3899C7.46484 30.0419 6.71295 25.3865 11.0078 21.0017Z"
                                  fill=""
                                />
                                <path
                                  d="M22.9536 19.7721C19.3669 19.4729 16.2493 19.7755 13.5603 21.9951C9.06026 25.5996 9.92627 30.4528 9.92627 36.39C9.92627 37.9608 11.2043 39.2627 12.799 39.2627C30.1143 39.2627 30.8035 39.8213 31.8302 37.5474C32.167 36.7785 32.0747 37.0229 32.0747 29.6667C32.0747 23.8239 27.0156 19.7721 22.9536 19.7721Z"
                                  fill=""
                                />
                                <path
                                  d="M36.43 19.7392C34.3653 19.7392 32.7658 19.5485 30.9922 21.0016C35.2549 25.3539 34.5351 29.6916 34.5351 36.3899C34.5351 36.7437 33.9607 36.6376 39.5013 36.6376C40.8794 36.6376 42 35.5212 42 34.1488V25.2755C42 22.2231 39.5013 19.7392 36.43 19.7392Z"
                                  fill=""
                                />
                              </g>
                              <defs>
                                <clipPath id="clip0_2623_21340">
                                  <rect width="42" height="42" fill="white" />
                                </clipPath>
                              </defs>
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {details.eventType === 'token' && (
                  <div className="row">
                    <div className="col-lg-4">
                      <div className={styles.eventcategorybox}>
                        <div className={styles.txt}>
                          <p>Minimum tokens to be purchased</p>
                          <h5>{details.minNumberOfTokens ?? '\u2014'}</h5>
                        </div>
                        <svg
                          className={styles.sm_icon}
                          width="42"
                          height="41"
                          viewBox="0 0 42 41"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M35.6669 17.808C32.6429 17.808 30.2909 15.456 30.2909 12.432V12.096C27.0989 10.752 26.0909 6.72 28.1069 3.864L29.1149 2.52C26.5949 1.344 23.7389 0.671997 20.8829 0.671997C9.7949 0.671997 0.7229 9.744 0.7229 20.832C0.7229 31.92 9.7949 40.992 20.8829 40.992C31.9709 40.992 41.0429 31.92 41.0429 20.832C41.0429 18.984 40.7069 16.968 40.2029 15.288C39.1949 16.968 37.5149 17.808 35.6669 17.808ZM20.7149 19.32C24.2429 19.32 27.0989 22.176 27.0989 25.704C27.0989 28.56 25.0829 31.08 22.3949 31.752V32.592C22.3949 33.6 21.7229 34.272 20.7149 34.272C19.7069 34.272 19.0349 33.6 19.0349 32.592V31.752C16.3469 31.08 14.3309 28.56 14.3309 25.704C14.3309 24.696 15.0029 24.024 16.0109 24.024C17.0189 24.024 17.6909 24.696 17.6909 25.704C17.6909 27.384 19.0349 28.728 20.7149 28.728C22.3949 28.728 23.7389 27.384 23.7389 25.704C23.7389 24.024 22.3949 22.68 20.7149 22.68C17.1869 22.68 14.3309 19.824 14.3309 16.296C14.3309 13.44 16.3469 10.92 19.0349 10.248V9.408C19.0349 8.4 19.7069 7.728 20.7149 7.728C21.7229 7.728 22.3949 8.4 22.3949 9.408V10.248C25.0829 10.92 27.0989 13.44 27.0989 16.296C27.0989 17.304 26.4269 17.976 25.4189 17.976C24.4109 17.976 23.7389 17.304 23.7389 16.296C23.7389 14.616 22.3949 13.272 20.7149 13.272C19.0349 13.272 17.6909 14.616 17.6909 16.296C17.6909 17.976 19.0349 19.32 20.7149 19.32Z"
                            fill="#FF9922"
                          />
                          <path
                            d="M37.1788 1.68C36.3388 0.671996 34.6588 0.671996 33.8188 1.68L30.6268 6.048C29.6188 7.392 30.6268 9.24 32.3068 9.24H33.4828V12.432C33.4828 13.608 34.3228 14.448 35.4988 14.448C36.6748 14.448 37.5148 13.608 37.5148 12.432V9.24H38.6908C39.5308 9.24 40.2028 8.736 40.5388 8.064C40.8748 7.392 40.8748 6.552 40.3708 5.88L37.1788 1.68Z"
                            fill="#FF9922"
                          />
                        </svg>

                        <div className={styles.bg_icons_evnt}>
                          <svg
                            width="42"
                            height="41"
                            viewBox="0 0 42 41"
                            fill="#FBFBFB"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M35.6669 17.808C32.6429 17.808 30.2909 15.456 30.2909 12.432V12.096C27.0989 10.752 26.0909 6.72 28.1069 3.864L29.1149 2.52C26.5949 1.344 23.7389 0.671997 20.8829 0.671997C9.7949 0.671997 0.7229 9.744 0.7229 20.832C0.7229 31.92 9.7949 40.992 20.8829 40.992C31.9709 40.992 41.0429 31.92 41.0429 20.832C41.0429 18.984 40.7069 16.968 40.2029 15.288C39.1949 16.968 37.5149 17.808 35.6669 17.808ZM20.7149 19.32C24.2429 19.32 27.0989 22.176 27.0989 25.704C27.0989 28.56 25.0829 31.08 22.3949 31.752V32.592C22.3949 33.6 21.7229 34.272 20.7149 34.272C19.7069 34.272 19.0349 33.6 19.0349 32.592V31.752C16.3469 31.08 14.3309 28.56 14.3309 25.704C14.3309 24.696 15.0029 24.024 16.0109 24.024C17.0189 24.024 17.6909 24.696 17.6909 25.704C17.6909 27.384 19.0349 28.728 20.7149 28.728C22.3949 28.728 23.7389 27.384 23.7389 25.704C23.7389 24.024 22.3949 22.68 20.7149 22.68C17.1869 22.68 14.3309 19.824 14.3309 16.296C14.3309 13.44 16.3469 10.92 19.0349 10.248V9.408C19.0349 8.4 19.7069 7.728 20.7149 7.728C21.7229 7.728 22.3949 8.4 22.3949 9.408V10.248C25.0829 10.92 27.0989 13.44 27.0989 16.296C27.0989 17.304 26.4269 17.976 25.4189 17.976C24.4109 17.976 23.7389 17.304 23.7389 16.296C23.7389 14.616 22.3949 13.272 20.7149 13.272C19.0349 13.272 17.6909 14.616 17.6909 16.296C17.6909 17.976 19.0349 19.32 20.7149 19.32Z"
                              fill=""
                            />
                            <path
                              d="M37.1788 1.68C36.3388 0.671996 34.6588 0.671996 33.8188 1.68L30.6268 6.048C29.6188 7.392 30.6268 9.24 32.3068 9.24H33.4828V12.432C33.4828 13.608 34.3228 14.448 35.4988 14.448C36.6748 14.448 37.5148 13.608 37.5148 12.432V9.24H38.6908C39.5308 9.24 40.2028 8.736 40.5388 8.064C40.8748 7.392 40.8748 6.552 40.3708 5.88L37.1788 1.68Z"
                              fill=""
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-4 mt-4 mt-lg-0">
                      <div className={styles.eventcategorybox}>
                        <div className={styles.txt}>
                          <p>Total Tokens Collected</p>
                          <h5>{tokens ?? '\u2014'}</h5>
                        </div>

                        <svg
                          className={styles.sm_icon}
                          xmlns="http://www.w3.org/2000/svg"
                          width="42"
                          height="42"
                          viewBox="0 0 42 42"
                          fill="none"
                        >
                          <path
                            d="M5.25 22.3125H2.625V38.0625H5.25C6.0375 38.0625 6.5625 37.5375 6.5625 36.75V23.625C6.5625 22.8375 6.0375 22.3125 5.25 22.3125Z"
                            fill="#3F9AE0"
                          />
                          <path
                            d="M37.9312 24.0187C37.0125 23.4937 35.8313 23.3625 34.9125 23.8875L26.25 28.6125C25.9875 30.1875 24.675 31.5 22.9688 31.5H17.7188C17.325 31.5 17.0625 31.2375 17.0625 30.8437C17.0625 30.45 17.325 30.1875 17.7188 30.1875H22.9688C24.0187 30.1875 24.9375 29.2687 24.9375 28.2187C24.9375 27.1687 24.15 26.25 22.9688 26.25C22.575 26.25 16.275 26.25 16.275 26.25L14.0437 24.0187C13.7812 23.7562 13.5188 23.625 13.125 23.625H7.875V36.75H10.2375L14.4375 38.0625C16.1438 38.5875 18.1125 39.375 20.2125 39.375C22.05 39.375 24.0187 38.7187 25.5938 37.5375L38.0625 29.2687C38.85 28.7437 39.375 27.6937 39.375 26.775C39.375 25.5937 38.85 24.675 37.9312 24.0187Z"
                            fill="#3F9AE0"
                          />
                          <path
                            d="M15.75 23.625C17.9246 23.625 19.6875 18.924 19.6875 13.125C19.6875 7.32601 17.9246 2.625 15.75 2.625C13.5754 2.625 11.8125 7.32601 11.8125 13.125C11.8125 18.924 13.5754 23.625 15.75 23.625Z"
                            fill="#3F9AE0"
                          />
                          <path
                            d="M20.7375 9.1875H23.3625C23.1 7.6125 22.7062 6.3 22.3125 5.25H19.6875C20.2125 6.43125 20.475 7.74375 20.7375 9.1875Z"
                            fill="#3F9AE0"
                          />
                          <path
                            d="M21.6566 3.9375C21.0004 3.15 20.3441 2.625 19.6879 2.625H18.2441C18.5066 3.01875 18.9004 3.4125 19.1629 3.9375H21.6566Z"
                            fill="#3F9AE0"
                          />
                          <path
                            d="M23.3621 17.0625H20.7371C20.4746 18.5063 20.2121 19.8188 19.8184 21H22.3121C22.7059 19.95 23.0996 18.6375 23.3621 17.0625Z"
                            fill="#3F9AE0"
                          />
                          <path
                            d="M18.2441 23.625H19.6879C20.3441 23.625 21.0004 23.1 21.6566 22.3125H19.1629C18.9004 22.8375 18.6379 23.2312 18.2441 23.625Z"
                            fill="#3F9AE0"
                          />
                          <path
                            d="M21.0004 13.125C21.0004 14.0438 21.0004 14.8313 20.8691 15.75H23.4941C23.6254 14.9625 23.6254 14.0438 23.6254 13.125C23.6254 12.2063 23.6254 11.2875 23.4941 10.5H20.8691C21.0004 11.4187 21.0004 12.2063 21.0004 13.125Z"
                            fill="#3F9AE0"
                          />
                          <path
                            d="M28.875 23.625C31.0496 23.625 32.8125 18.924 32.8125 13.125C32.8125 7.32601 31.0496 2.625 28.875 2.625C26.7004 2.625 24.9375 7.32601 24.9375 13.125C24.9375 18.924 26.7004 23.625 28.875 23.625Z"
                            fill="#3F9AE0"
                          />
                          <path
                            d="M33.8625 9.1875H36.4875C36.225 7.6125 35.8312 6.3 35.4375 5.25H32.8125C33.3375 6.43125 33.6 7.74375 33.8625 9.1875Z"
                            fill="#3F9AE0"
                          />
                          <path
                            d="M34.7816 3.9375C34.1254 3.15 33.4691 2.625 32.8129 2.625H31.3691C31.6316 3.01875 32.0254 3.4125 32.2879 3.9375H34.7816Z"
                            fill="#3F9AE0"
                          />
                          <path
                            d="M36.4871 17.0625H33.8621C33.5996 18.5063 33.3371 19.8188 32.9434 21H35.4371C35.8309 19.95 36.2246 18.6375 36.4871 17.0625Z"
                            fill="#3F9AE0"
                          />
                          <path
                            d="M31.3691 23.625H32.8129C33.4691 23.625 34.1254 23.1 34.7816 22.3125H32.2879C32.0254 22.8375 31.7629 23.2312 31.3691 23.625Z"
                            fill="#3F9AE0"
                          />
                          <path
                            d="M34.1254 13.125C34.1254 14.0438 34.1254 14.8313 33.9941 15.75H36.6191C36.7504 14.9625 36.7504 14.0438 36.7504 13.125C36.7504 12.2063 36.7504 11.2875 36.6191 10.5H33.9941C34.1254 11.4187 34.1254 12.2063 34.1254 13.125Z"
                            fill="#3F9AE0"
                          />
                        </svg>

                        <div className={styles.bg_icons_evnt}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="42"
                            height="42"
                            viewBox="0 0 42 42"
                            fill="#FBFBFB"
                          >
                            <path
                              d="M5.25 22.3125H2.625V38.0625H5.25C6.0375 38.0625 6.5625 37.5375 6.5625 36.75V23.625C6.5625 22.8375 6.0375 22.3125 5.25 22.3125Z"
                              fill=""
                            />
                            <path
                              d="M37.9312 24.0187C37.0125 23.4937 35.8313 23.3625 34.9125 23.8875L26.25 28.6125C25.9875 30.1875 24.675 31.5 22.9688 31.5H17.7188C17.325 31.5 17.0625 31.2375 17.0625 30.8437C17.0625 30.45 17.325 30.1875 17.7188 30.1875H22.9688C24.0187 30.1875 24.9375 29.2687 24.9375 28.2187C24.9375 27.1687 24.15 26.25 22.9688 26.25C22.575 26.25 16.275 26.25 16.275 26.25L14.0437 24.0187C13.7812 23.7562 13.5188 23.625 13.125 23.625H7.875V36.75H10.2375L14.4375 38.0625C16.1438 38.5875 18.1125 39.375 20.2125 39.375C22.05 39.375 24.0187 38.7187 25.5938 37.5375L38.0625 29.2687C38.85 28.7437 39.375 27.6937 39.375 26.775C39.375 25.5937 38.85 24.675 37.9312 24.0187Z"
                              fill=""
                            />
                            <path
                              d="M15.75 23.625C17.9246 23.625 19.6875 18.924 19.6875 13.125C19.6875 7.32601 17.9246 2.625 15.75 2.625C13.5754 2.625 11.8125 7.32601 11.8125 13.125C11.8125 18.924 13.5754 23.625 15.75 23.625Z"
                              fill=""
                            />
                            <path
                              d="M20.7375 9.1875H23.3625C23.1 7.6125 22.7062 6.3 22.3125 5.25H19.6875C20.2125 6.43125 20.475 7.74375 20.7375 9.1875Z"
                              fill=""
                            />
                            <path
                              d="M21.6566 3.9375C21.0004 3.15 20.3441 2.625 19.6879 2.625H18.2441C18.5066 3.01875 18.9004 3.4125 19.1629 3.9375H21.6566Z"
                              fill=""
                            />
                            <path
                              d="M23.3621 17.0625H20.7371C20.4746 18.5063 20.2121 19.8188 19.8184 21H22.3121C22.7059 19.95 23.0996 18.6375 23.3621 17.0625Z"
                              fill=""
                            />
                            <path
                              d="M18.2441 23.625H19.6879C20.3441 23.625 21.0004 23.1 21.6566 22.3125H19.1629C18.9004 22.8375 18.6379 23.2312 18.2441 23.625Z"
                              fill=""
                            />
                            <path
                              d="M21.0004 13.125C21.0004 14.0438 21.0004 14.8313 20.8691 15.75H23.4941C23.6254 14.9625 23.6254 14.0438 23.6254 13.125C23.6254 12.2063 23.6254 11.2875 23.4941 10.5H20.8691C21.0004 11.4187 21.0004 12.2063 21.0004 13.125Z"
                              fill=""
                            />
                            <path
                              d="M28.875 23.625C31.0496 23.625 32.8125 18.924 32.8125 13.125C32.8125 7.32601 31.0496 2.625 28.875 2.625C26.7004 2.625 24.9375 7.32601 24.9375 13.125C24.9375 18.924 26.7004 23.625 28.875 23.625Z"
                              fill=""
                            />
                            <path
                              d="M33.8625 9.1875H36.4875C36.225 7.6125 35.8312 6.3 35.4375 5.25H32.8125C33.3375 6.43125 33.6 7.74375 33.8625 9.1875Z"
                              fill=""
                            />
                            <path
                              d="M34.7816 3.9375C34.1254 3.15 33.4691 2.625 32.8129 2.625H31.3691C31.6316 3.01875 32.0254 3.4125 32.2879 3.9375H34.7816Z"
                              fill=""
                            />
                            <path
                              d="M36.4871 17.0625H33.8621C33.5996 18.5063 33.3371 19.8188 32.9434 21H35.4371C35.8309 19.95 36.2246 18.6375 36.4871 17.0625Z"
                              fill=""
                            />
                            <path
                              d="M31.3691 23.625H32.8129C33.4691 23.625 34.1254 23.1 34.7816 22.3125H32.2879C32.0254 22.8375 31.7629 23.2312 31.3691 23.625Z"
                              fill=""
                            />
                            <path
                              d="M34.1254 13.125C34.1254 14.0438 34.1254 14.8313 33.9941 15.75H36.6191C36.7504 14.9625 36.7504 14.0438 36.7504 13.125C36.7504 12.2063 36.7504 11.2875 36.6191 10.5H33.9941C34.1254 11.4187 34.1254 12.2063 34.1254 13.125Z"
                              fill=""
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-4 mt-4 mt-lg-0">
                      <div className={styles.eventcategorybox}>
                        <div className={styles.txt}>
                          <p>Value Per Token</p>
                          <h5>
                            {details.amountPerToken != null && details.amountPerToken !== ''
                              ? `$${details.amountPerToken}`
                              : '\u2014'}
                          </h5>
                        </div>

                        <svg
                          className={styles.sm_icon}
                          xmlns="http://www.w3.org/2000/svg"
                          width="42"
                          height="42"
                          viewBox="0 0 42 42"
                          fill="none"
                        >
                          <path
                            d="M35.9518 17.808C32.9278 17.808 30.5758 15.456 30.5758 12.432V12.096C27.3838 10.752 26.3758 6.72 28.3918 3.864L29.3998 2.52C26.8798 1.344 24.0238 0.671997 21.1678 0.671997C10.0798 0.671997 1.00781 9.744 1.00781 20.832C1.00781 31.92 10.0798 40.992 21.1678 40.992C32.2558 40.992 41.3278 31.92 41.3278 20.832C41.3278 18.984 40.9918 16.968 40.4878 15.288C39.4798 16.968 37.7998 17.808 35.9518 17.808ZM20.9998 19.32C24.5278 19.32 27.3838 22.176 27.3838 25.704C27.3838 28.56 25.3678 31.08 22.6798 31.752V32.592C22.6798 33.6 22.0078 34.272 20.9998 34.272C19.9918 34.272 19.3198 33.6 19.3198 32.592V31.752C16.6318 31.08 14.6158 28.56 14.6158 25.704C14.6158 24.696 15.2878 24.024 16.2958 24.024C17.3038 24.024 17.9758 24.696 17.9758 25.704C17.9758 27.384 19.3198 28.728 20.9998 28.728C22.6798 28.728 24.0238 27.384 24.0238 25.704C24.0238 24.024 22.6798 22.68 20.9998 22.68C17.4718 22.68 14.6158 19.824 14.6158 16.296C14.6158 13.44 16.6318 10.92 19.3198 10.248V9.408C19.3198 8.4 19.9918 7.728 20.9998 7.728C22.0078 7.728 22.6798 8.4 22.6798 9.408V10.248C25.3678 10.92 27.3838 13.44 27.3838 16.296C27.3838 17.304 26.7118 17.976 25.7038 17.976C24.6958 17.976 24.0238 17.304 24.0238 16.296C24.0238 14.616 22.6798 13.272 20.9998 13.272C19.3198 13.272 17.9758 14.616 17.9758 16.296C17.9758 17.976 19.3198 19.32 20.9998 19.32Z"
                            fill="#FF7222"
                          />
                          <path
                            d="M37.4639 1.68C36.6239 0.671996 34.9439 0.671996 34.1039 1.68L30.9119 6.048C29.9039 7.392 30.9119 9.24 32.5919 9.24H33.7679V12.432C33.7679 13.608 34.6079 14.448 35.7839 14.448C36.9599 14.448 37.7999 13.608 37.7999 12.432V9.24H38.9759C39.8159 9.24 40.4879 8.736 40.8239 8.064C41.1599 7.392 41.1599 6.552 40.6559 5.88L37.4639 1.68Z"
                            fill="#FF7222"
                          />
                        </svg>

                        <div className={styles.bg_icons_evnt}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="42"
                            height="42"
                            viewBox="0 0 42 42"
                            fill="#FBFBFB"
                          >
                            <path
                              d="M35.9518 17.808C32.9278 17.808 30.5758 15.456 30.5758 12.432V12.096C27.3838 10.752 26.3758 6.72 28.3918 3.864L29.3998 2.52C26.8798 1.344 24.0238 0.671997 21.1678 0.671997C10.0798 0.671997 1.00781 9.744 1.00781 20.832C1.00781 31.92 10.0798 40.992 21.1678 40.992C32.2558 40.992 41.3278 31.92 41.3278 20.832C41.3278 18.984 40.9918 16.968 40.4878 15.288C39.4798 16.968 37.7998 17.808 35.9518 17.808ZM20.9998 19.32C24.5278 19.32 27.3838 22.176 27.3838 25.704C27.3838 28.56 25.3678 31.08 22.6798 31.752V32.592C22.6798 33.6 22.0078 34.272 20.9998 34.272C19.9918 34.272 19.3198 33.6 19.3198 32.592V31.752C16.6318 31.08 14.6158 28.56 14.6158 25.704C14.6158 24.696 15.2878 24.024 16.2958 24.024C17.3038 24.024 17.9758 24.696 17.9758 25.704C17.9758 27.384 19.3198 28.728 20.9998 28.728C22.6798 28.728 24.0238 27.384 24.0238 25.704C24.0238 24.024 22.6798 22.68 20.9998 22.68C17.4718 22.68 14.6158 19.824 14.6158 16.296C14.6158 13.44 16.6318 10.92 19.3198 10.248V9.408C19.3198 8.4 19.9918 7.728 20.9998 7.728C22.0078 7.728 22.6798 8.4 22.6798 9.408V10.248C25.3678 10.92 27.3838 13.44 27.3838 16.296C27.3838 17.304 26.7118 17.976 25.7038 17.976C24.6958 17.976 24.0238 17.304 24.0238 16.296C24.0238 14.616 22.6798 13.272 20.9998 13.272C19.3198 13.272 17.9758 14.616 17.9758 16.296C17.9758 17.976 19.3198 19.32 20.9998 19.32Z"
                              fill=""
                            />
                            <path
                              d="M37.4639 1.68C36.6239 0.671996 34.9439 0.671996 34.1039 1.68L30.9119 6.048C29.9039 7.392 30.9119 9.24 32.5919 9.24H33.7679V12.432C33.7679 13.608 34.6079 14.448 35.7839 14.448C36.9599 14.448 37.7999 13.608 37.7999 12.432V9.24H38.9759C39.8159 9.24 40.4879 8.736 40.8239 8.064C41.1599 7.392 41.1599 6.552 40.6559 5.88L37.4639 1.68Z"
                              fill=""
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="row">
                  <h4 className={styles.vendorsty}>Vendors</h4>
                  {details.vendorsAdded?.map(
                    (vendor) =>
                      vendor?.isDeleted === false && (
                        <div
                          key={vendor?._id || vendor?.id}
                          className="col-lg-4"
                          style={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/vendorusers/viewdetails/${vendor?._id}`)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              navigate(`/vendor/viewdetails/${vendor?._id}`);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <div className={styles.vendorssec}>
                            <Image src={vendor?.image ? vendor?.image : defaultImg} alt="" />
                            <div className={styles.vendors_box}>
                              <h5>{vendor?.businessName}</h5>
                              <h6>Tokens Collected : {vendor?.wallet?.tokensLeft}</h6>
                              <p>{vendor?.category}</p>
                            </div>
                          </div>
                        </div>
                      )
                  )}
                  {/* <div className="col-lg-4">
                  <div className={styles.vendorssec}>
                    <Image src={vendors1} alt="" />
                    <div className={styles.vendors_box}>
                      <h5>Planet Of The Crepes</h5>
                      <h6>Tokens Collected : 20</h6>
                      <p>Groceries</p>
                    </div>
                  </div>
                </div>
                <div className="col-lg-4">
                  <div className={styles.vendorssec}>
                    <Image src={vendors1} alt="" />
                    <div className={styles.vendors_box}>
                      <h5>Planet Of The Crepes</h5>
                      <h6>Tokens Collected : 20</h6>
                      <p>Groceries</p>
                    </div>
                  </div>
                </div>
                <div className="col-lg-4">
                  <div className={styles.vendorssec}>
                    <Image src={vendors1} alt="" />
                    <div className={styles.vendors_box}>
                      <h5>Planet Of The Crepes</h5>
                      <h6>Tokens Collected : 20</h6>
                      <p>Groceries</p>
                    </div>
                  </div>
                </div> */}
                </div>
                {/* <div className="row mt-3">
                <div className="col-lg-4">
                  <div className={styles.vendorssec}>
                    <Image src={vendors1} alt="" />
                    <div className={styles.vendors_box}>
                      <h5>Planet Of The Crepes</h5>
                      <h6>Tokens Collected : 20</h6>
                      <p>Groceries</p>
                    </div>
                  </div>
                </div>
                <div className="col-lg-4">
                  <div className={styles.vendorssec}>
                    <Image src={vendors1} alt="" />
                    <div className={styles.vendors_box}>
                      <h5>Planet Of The Crepes</h5>
                      <h6>Tokens Collected : 20</h6>
                      <p>Groceries</p>
                    </div>
                  </div>
                </div>
                <div className="col-lg-4">
                  <div className={styles.vendorssec}>
                    <Image src={vendors1} alt="" />
                    <div className={styles.vendors_box}>
                      <h5>Planet Of The Crepes</h5>
                      <h6>Tokens Collected : 20</h6>
                      <p>Groceries</p>
                    </div>
                  </div>
                </div>
              </div> */}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

export default ViewEvent;
